import { type FamilyRelativeRow as FamilyRelativeRowData, familyRelativeRecords } from '@liza/fhir-utils';
import { useTranslation } from 'react-i18next';
import { FamilyHistoryGroupRow } from './FamilyHistoryGroupRow';
import { conditionLine, type FamilyHistoryItemProps, relativeTitle } from './familyHistoryRows';
import { FamilyMemberRecordItem } from './FamilyMemberRecordItem';
import { formatDeceased } from './familyMemberHistoryUtils';
import { RecordListItem } from './RecordListItem';

export type FamilyRelativeRowProps = FamilyHistoryItemProps & { row: FamilyRelativeRowData };

/** A relative with conditions: a plain row when one resource describes them, otherwise a header
 * carrying the curated condition list that expands to the source resources. */
export const FamilyRelativeRow = ({ row, ...itemProps }: FamilyRelativeRowProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const records = familyRelativeRecords(row.relative);
  const title = relativeTitle(row.relative, t);
  const conditions = row.conditions.map((fact) => conditionLine(fact, t)).join(', ');
  const { onSelectFamilyMember, selectedFamilyMemberId, onPromotionClick, getPromotionStatus } = itemProps;

  if (records.length === 1) {
    const record = row.relative.record;
    return (
      <RecordListItem
        testId="family-member-item"
        title={title}
        description={[formatDeceased(record, t), conditions].filter(Boolean).join(' · ')}
        onClick={onSelectFamilyMember ? (): void => onSelectFamilyMember(record.id) : undefined}
        selected={selectedFamilyMemberId === record.id}
        onPromotionClick={onPromotionClick ? (): void => onPromotionClick(record.id) : undefined}
        promotionStatus={getPromotionStatus?.(record.id)}
      />
    );
  }

  return (
    <FamilyHistoryGroupRow
      title={title}
      description={conditions}
      count={records.length}
      ariaLabel={t('showFamilyMemberRecords', { name: title })}
      testId={`family-relative-${row.relative.key}`}
      recordIds={records.map((r) => r.id)}
      selectedId={selectedFamilyMemberId}
    >
      {records.map((record) => (
        <FamilyMemberRecordItem key={record.id} record={record} {...itemProps} />
      ))}
    </FamilyHistoryGroupRow>
  );
};
